import React, { useEffect, useState, useMemo } from 'react'
import './digital-vault-search.css'
import { Container, Grid, useMediaQuery } from '@material-ui/core'
import { ButtonHLPure, DropDownBtnHLPure, SearchBarHLPure, SimpleLoading, GridSelectionHLPure, InputSwitchHLPure, DialogDetailsPure, TextAreaHLPure, CheckBoxHLPure } from '../../pures'
import { filterBy } from '@progress/kendo-data-query'
import { useTranslation } from 'react-i18next'
import { DigitalVaultDocumentAction } from '../../actions'
import { NIL } from 'uuid'
import AdditionalFilters from './components/AdditionalFilters'

/**
 * Component to display document details in a formatted list
 */
const ChildrenDialogDetails = ({ details }) => {
  return (
    <div className='syncfusionFileManager-dialog-details'>
      <ul>
        {details.map((detail, index) => {
          if (detail.line) {
            return <hr key={index} className='syncfusionFileManager-hr' />
          } else {
            return (
              <li key={index}>
                <span className='syncfusionFileManager-labelKey'>{detail.label}: <span className='syncfusionFileManager-labelValue'>{detail.value}</span></span>
              </li>
            )
          }
        })}
      </ul>
    </div>
  )
}

const initialDataState = {
  skip: 0,
  take: 6,
};

const initialFilter = {
  logic: 'and',
  filters: [],
};

const formatDateWithMonthAbbreviation = (date) => {
  if (!date) return '—'

  const monthsAbbr = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

  const day = date.getDate()
  const month = monthsAbbr[date.getMonth()]
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

const DigitalVaultSearchView = (props) => {
  const {
    isLoading,
    companies,
    selectedCompanyId,
    selectedCategoryId,
    documents,
    loadDocuments,
    noDataMessage,
    selectedDocument,
    setSelectedDocument,
    filterCategories,
    filterTypeDocuments,
    filterUsers,
    filterCreatedDates
  } = props;

  const { t, i18n } = useTranslation()

  const [ddCompanyId, setDdCompanyId] = useState(selectedCompanyId)
  const [ddCategoryId, setDdCategoryId] = useState(selectedCategoryId)

  const [searchText, setSearchText] = useState('')
  const [isIntelligentSearch, setIsIntelligentSearch] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [detailsData, setDetailsData] = useState([])
  const [hasSearched, setHasSearched] = useState(false)

  // Estados para mensajes de error
  const [showCompanyError, setShowCompanyError] = useState(false)
  const [showSearchTextError, setShowSearchTextError] = useState(false)

  // Estados para los filtros
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedTypeDocument, setSelectedTypeDocument] = useState('')
  const [selectedCreatedDate, setSelectedCreatedDate] = useState('')
  const [selectedUser, setSelectedUser] = useState('')
  const [filterIsValid, setFilterIsValid] = useState(false)
  const [filterSigningInGovo, setFilterSigningInGovo] = useState(false)
  const [selectSignGovoOptions, setSelectSignGovoOptions] = useState([])
  const [selectedNotifyOptions, setSelectedNotifyOptions] = useState([])

  // Estado para el grid (requerido por cellActions)
  const [showDetails, setShowDetails] = useState(false)
  const [nameComponentUser, setNameComponentUser] = useState('')

  // Estados para checkbox 'any' | 'all' | 'exact'
  const [searchMode, setSearchMode] = useState('any')

  const showArrowBtn = useMediaQuery('(max-width: 960px)')
  const showMobile = useMediaQuery('(max-width: 560px)')
  const isMobileVertical = useMediaQuery('(max-width: 560px) and (orientation: portrait)')
  const is4K = useMediaQuery('(max-width: 1920px)')
  const isFullHD = useMediaQuery('(max-width: 1919px)')
  const isLaptop = useMediaQuery('(max-width: 1599px)')
  const isTablet = useMediaQuery('(max-width: 1365px)')
  const isMobile = useMediaQuery('(max-width: 767px)')

  const showOnlyIcons = useMemo(() => {
    if (isMobileVertical) return true

    if (isTablet || isMobile) return true

    return false
  }, [isMobileVertical, isTablet, isMobile])

  const widthColumnFive = useMemo(() => {
    if (isMobileVertical) return '60px'

    if (showOnlyIcons) return '80px'

    if (is4K) return '50px'
    if (isFullHD) return '60px'
    if (isLaptop) return '60px'

    return '120px'
  }, [is4K, isFullHD, isLaptop, isMobileVertical, showOnlyIcons])

  const minResizableWidth5 = useMemo(() => {
    if (showArrowBtn || isMobileVertical) return '45px'
    if (showOnlyIcons) return '80px'
    return '120px'
  }, [showArrowBtn, isMobileVertical, showOnlyIcons])




  const onCompanyDropdownChange = (e) => {
    setDdCompanyId(e.target.value)
    if (e.target.value !== 0) {
      setShowCompanyError(false)
    }
    handleClearFilters()
  }

  const handleOpenDocument = (item) => {
    setSelectedDocument(item)
  }

  // Función para limpiar todos los filtros
  const handleClearFilters = () => {
    setSelectedCategory('')
    setSelectedTypeDocument('')
    setSelectedCreatedDate('')
    setSelectedUser('')
    setFilterIsValid(false)
    setFilterSigningInGovo(false)
  }

  // Verificar si hay algún filtro activo
  const hasActiveFilters = () => {
    return selectedCategory !== '' ||
      selectedTypeDocument !== '' ||
      selectedCreatedDate !== '' ||
      selectedUser !== '' ||
      filterIsValid ||
      filterSigningInGovo
  }

  const handleSearch = async () => {
    let hasError = false

    if (!ddCompanyId || ddCompanyId === '') {
      setShowCompanyError(true)
      hasError = true
    } else {
      setShowCompanyError(false)
    }

    if (!searchText || searchText.trim() === '') {
      setShowSearchTextError(true)
      hasError = true
    } else {
      setShowSearchTextError(false)
    }

    if (hasError) return
    handleClearFilters()

    setHasSearched(true)
    const globalSearch = ddCompanyId === NIL
    await loadDocuments(ddCompanyId, ddCategoryId, searchText, isIntelligentSearch, undefined, globalSearch, searchMode)
  }

  useEffect(() => {
    if (hasSearched && searchText.trim() !== '') {
      const globalSearch = ddCompanyId === NIL
      loadDocuments(ddCompanyId, ddCategoryId, searchText, isIntelligentSearch, undefined, globalSearch, searchMode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntelligentSearch])

  const isDateInRange = (date, rangeId) => {
    if (!date) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const docDate = new Date(date)
    docDate.setHours(0, 0, 0, 0)

    const diffTime = today.getTime() - docDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    switch (rangeId) {
      case 'today':
        return diffDays === 0
      case 'yesterday':
        return diffDays === 1
      case 'last7days':
        return diffDays >= 0 && diffDays <= 7
      case 'last30days':
        return diffDays >= 0 && diffDays <= 30
      case 'thisMonth':
        return docDate.getMonth() === today.getMonth() && docDate.getFullYear() === today.getFullYear()
      case 'thisYear':
        return docDate.getFullYear() === today.getFullYear()
      case 'beforeThisYear':
        return docDate.getFullYear() < today.getFullYear()
      default:
        return true
    }
  }

  // Preparar opciones para los dropdowns
  const categoryOptions = [
    { id: '', name: t('digital.vault.search.document.filter.category') },
    ...(filterCategories || []).map((cat, index) => ({ id: cat, name: cat }))
  ]

  const typeDocumentOptions = [
    { id: '', name: t('digital.vault.search.document.filter.type.document') },
    ...(filterTypeDocuments || []).map((type, index) => ({ id: type, name: type }))
  ]

  const createdDateOptions = [
    { id: '', name: t('digital.vault.search.document.filter.start.date') },
    { id: 'today', name: t('digital.vault.search.today') },
    { id: 'yesterday', name: t('digital.vault.search.yesterday') },
    { id: 'last7days', name: t('digital.vault.search.last7days') },
    { id: 'last30days', name: t('digital.vault.search.last30days') },
    { id: 'thisMonth', name: t('digital.vault.search.thisMonth') },
    { id: 'thisYear', name: t('digital.vault.search.thisYear') },
    { id: 'beforeThisYear', name: t('digital.vault.search.beforeThisYear') }
  ]

  const userOptions = [
    { id: '', name: t('digital.vault.search.document.filter.user') },
    ...(filterUsers || []).map((user, index) => ({ id: user, name: user }))
  ]

  const notifyOptions = [
    { id: '', name: t('digital.vault.search.document.filter.notify') },
    ...(filterUsers || []).map((user, index) => ({ id: user, name: user }))
  ]

  const signGovoOptions = [
    { id: '', name: t('digital.vault.search.document.filter.sign.govo') },
    ...(filterUsers || []).map((user, index) => ({ id: user, name: user }))
  ]

  // Mapeo de campos del documento a etiquetas para mostrar en el modal
  const keyLabelsMap = [
    {
      key: 'documentName',
      label: t('digital.vault.search.document.name'),
    },
    {
      key: 'userName',
      label: t('digital.vault.search.propietary'),
    },
    {
      line: true
    },
    {
      key: 'documentStartDate',
      label: t('digital.vault.search.document.start.date'),
    },
    {
      key: 'documentValidation',
      label: t('digital.vault.search.document.expiration.date'),
    },
    {
      line: true
    },
    {
      key: 'typeDocumentInfoType',
      label: t('digital.vault.search.document.type.document'),
    },
    {
      key: 'confidential',
      label: t('digital.vault.search.document.confidential'),
    },
    {
      line: true
    },
    {
      key: 'description',
      label: t('digital.vault.search.document.description'),
    },
    {
      key: 'summary',
      label: t('digital.vault.search.document.summary'),
    },
    {
      line: true
    }
  ]

  useEffect(() => {
    setDdCompanyId(selectedCompanyId)
  }, [selectedCompanyId])

  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(initialDataState);
  const handlePageChange = (e) => {
    setPage({
      skip: e.skip,
      take: e.take,
    });
  };

  const iconGridInfo = `${process.env.REACT_APP_AWS_S3_BUCKET_URL_ICONS}${process.env.REACT_APP_ICONS_DIGITAL_ICON_DISABLED}`
  const iconFilter = `${process.env.REACT_APP_AWS_S3_BUCKET_URL_ICONS}${process.env.REACT_APP_ICONS_FILTER_ICON}`

  // Función para formatear el valor de un campo del documento
  const formatDetailValue = (key, value) => {
    if (value === null || value === undefined) return ''

    if (key === 'documentStartDate' || key === 'documentValidation') {
      return value ? value.toLocaleDateString('es-MX') : ''
    }

    if (key === 'mandatory' || key === 'penalized' || key === 'confidential') {
      return value ? t('common.yes') : t('common.no')
    }

    return value || ''
  }

  // Función para preparar los detalles del documento para mostrar en el modal
  const getDocumentDetails = (document) => {
    const details = []

    keyLabelsMap.forEach((item) => {
      if (item.line) {
        details.push({ line: true })
      } else {
        const value = formatDetailValue(item.key, document[item.key])
        details.push({
          label: item.label,
          value: value
        })
      }
    })

    return details
  }

  // Función para manejar el clic en el icono de información
  const handleInfoIconClick = (document) => {
    const details = getDocumentDetails(document)
    setDetailsData(details)
    setShowInfoModal(true)
  }

  // Función para cerrar el modal de información
  const handleCloseInfoModal = () => {
    setShowInfoModal(false)
    setDetailsData([])
  }

  // Crear el contenido hijo del diálogo con los detalles
  const childrenDialog = <ChildrenDialogDetails details={detailsData} />

  // Custom cell renderers for GridSelectionHLPure
  const cellColumnOne = (props) => (
    <>
      <td className='digitalVaultSearchView-columns-header-1'>
        <span className='digitalVaultSearchView-columns-document-name'>{props.dataItem.documentName}</span>
        <span className='digitalVaultSearchView-columns-date-register'>
          {t('digital.vault.search.document.registered.on')} {formatDateWithMonthAbbreviation(props.dataItem.documentStartDate)}
        </span>
      </td>
    </>

  )

  const cellColumnTwo = (props) => {
    const { dataItem } = props;

    const snippet = dataItem?.snippet || '';

    return (
      <td className='digitalVaultSearchView-columns-snippet'>
        <span
          className={
            isIntelligentSearch
              ? 'digitalVaultSearchView-snippet-text-semantic'
              : 'digitalVaultSearchView-snippet-text'
          }
          dangerouslySetInnerHTML={{ __html: snippet }}
        />
      </td>
    )
  }


  const headersColumns = (props) => (
    <th className='digitalVaultSearchView-headers'>{props.title}</th>
  )

  if (isLoading && companies.length === 0) return <SimpleLoading />

  if (selectedDocument) {
    return <DigitalVaultDocumentAction
      documentId={selectedDocument.documentId}
      stateToMaintain={{ selectedCompanyId, selectedCategoryId, selectedDocument }}
      goBack={() => setSelectedDocument(null)}
    />
  }

  // Aplicar filtros a los documentos
  let filteredDocuments = filterBy(documents, filter)

  // Aplicar filtros adicionales
  if (selectedCategory) {
    filteredDocuments = filteredDocuments.filter(doc => doc.categoryName === selectedCategory)
  }

  if (selectedTypeDocument) {
    filteredDocuments = filteredDocuments.filter(doc => doc.typeDocumentName === selectedTypeDocument)
  }

  if (selectedCreatedDate) {
    filteredDocuments = filteredDocuments.filter(doc =>
      isDateInRange(doc.documentStartDate, selectedCreatedDate)
    )
  }

  if (selectedUser) {
    filteredDocuments = filteredDocuments.filter(doc => doc.userName === selectedUser)
  }

  if (filterIsValid) {
    filteredDocuments = filteredDocuments.filter(doc => doc.documentValidation)
  }

  if (filterSigningInGovo) {
    filteredDocuments = filteredDocuments.filter(doc => doc.signingInGovo === true)
  }

  const data = filteredDocuments

  const noResultsStyles = {
    color: "#2C353C",
    fontSize: "16px",
    fontWeight: 600,
    textAlign: "center"
  }

  return (
    <Container disableGutters className='digitalVaultSearchView'>
      {noDataMessage ? (
        <p style={noResultsStyles}>
          {noDataMessage}
        </p>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className={`digitalVaultSearchView-container ${isIntelligentSearch ? 'intelligent-on' : ''}`}>
              <div class='digitalVaultSearchView-div1'>
                <DropDownBtnHLPure
                  optionsDropDown={companies}
                  optionsValue={ddCompanyId}
                  valueItem={'id'}
                  nameOption={'companyName'}
                  onDropDownChange={onCompanyDropdownChange}
                  showError={showCompanyError}
                  errorText={t('digital.vault.search.document.error.select.company')}
                  iconFilter={true}
                />
              </div>
              {!isIntelligentSearch && (
                <div className='digitalVaultSearchView-div2'>
                  <div className='checkbox-group'>
                    <div className='custom-checkbox-row'>
                      <CheckBoxHLPure
                        idCheckBox='anyWord'
                        checkedState={searchMode === 'any'}
                        onChangeHandler={() => setSearchMode('any')}
                      />
                      <span className='custom-checkbox-label'>
                        {t('digital.vault.search.document.anyWordOption')}
                      </span>
                    </div>

                    <div className='custom-checkbox-row'>
                      <CheckBoxHLPure
                        idCheckBox='allWords'
                        checkedState={searchMode === 'all'}
                        onChangeHandler={() => setSearchMode('all')}
                      />
                      <span className='custom-checkbox-label'>
                        {t('digital.vault.search.document.allWordsOption')}
                      </span>
                    </div>

                    <div className='custom-checkbox-row'>
                      <CheckBoxHLPure
                        idCheckBox='exactPhrase'
                        checkedState={searchMode === 'exact'}
                        onChangeHandler={() => setSearchMode('exact')}
                      />
                      <span className='custom-checkbox-label'>
                        {t('digital.vault.search.document.exactPhraseOption')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div class='digitalVaultSearchView-div3'>
                <div className={`digitalVaultSearchView-search-bar-container ${i18n.language === 'en' ? 'error-english' : 'error-spanish'}`}>
                  <SearchBarHLPure
                    placeholder={t('digitalVaultSearchView.grid.placeholder.searchText')}
                    disabled={false}
                    searchValue={searchText}
                    onSearchChange={(e) => {
                      setSearchText(e.target.value)
                      if (e.target.value.trim() !== '') {
                        setShowSearchTextError(false)
                      }
                    }}
                    asTextarea={isIntelligentSearch}
                    rows={4}
                    onKeyDown={(e) => {
                      if (isIntelligentSearch && e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSearch()
                      }
                    }}
                    onKeyPress={(e) => {
                      if (!isIntelligentSearch && e.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                    errorInput={showSearchTextError}
                    errorInputText={t('digital.vault.search.document.error.enter.search.text')}
                  />
                </div>
              </div>
              <div class='digitalVaultSearchView-div4'>
                <div className={`digitalVaultSearchView-filters-container-intelligent ${isIntelligentSearch ? 'active-intelligent' : ''}`}>
                  <div className='digitalVaultSearchView-filter-item intelligent-search-inline'>
                    <InputSwitchHLPure
                      labelInput={t('digitalVaultSearchView.intelligentSearch.label')}
                      checked={isIntelligentSearch}
                      onChange={(e) => { setIsIntelligentSearch(e.value) }}
                      showTooltip={true}
                      positionTooltip='top'
                      titleTooltip={t('digitalVaultSearchView.intelligentSearch.tooltipTitle')}
                      descriptionTooltip={t('digitalVaultSearchView.intelligentSearch.tooltipDescription')}
                      informationIcon={true}
                    />
                  </div>
                </div>
              </div>
              <div class='digitalVaultSearchView-div5'>
                <ButtonHLPure
                  nameBtn={t('button.search')}
                  primary
                  onClickBtn={handleSearch}
                />
              </div>
              <div class='digitalVaultSearchView-div6'>
                <AdditionalFilters
                  categoryOptions={categoryOptions}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  typeDocumentOptions={typeDocumentOptions}
                  selectedTypeDocument={selectedTypeDocument}
                  setSelectedTypeDocument={setSelectedTypeDocument}
                  createdDateOptions={createdDateOptions}
                  selectedCreatedDate={selectedCreatedDate}
                  setSelectedCreatedDate={setSelectedCreatedDate}
                  userOptions={userOptions}
                  selectedUser={selectedUser}
                  setSelectedUser={setSelectedUser}
                  filterIsValid={filterIsValid}
                  setFilterIsValid={setFilterIsValid}
                  filterSigningInGovo={filterSigningInGovo}
                  setFilterSigningInGovo={setFilterSigningInGovo}
                  hasActiveFilters={hasActiveFilters}
                  handleClearFilters={handleClearFilters}
                  iconFilter={iconFilter}
                  notifyOptions={notifyOptions}
                  signGovoOptions={signGovoOptions}
                  selectedSignGovoOption={selectSignGovoOptions}
                  setSelectSignGovoOptions={setSelectSignGovoOptions}
                  selectedNotifyOptions={selectedNotifyOptions}
                  setSelectedNotifyOptions={setSelectedNotifyOptions}
                />
              </div>
            </div>
          </Grid>

          <Grid item xs={12}>
            <GridSelectionHLPure
              pageable={true}
              skip={page.skip}
              take={page.take}
              dataGrid={data}
              filter={true}
              onFilterChange={(e) => setFilter(e.filter)}
              onSelectedItem={() => { }}
              setShowDetails={setShowDetails}
              setNameComponentUser={setNameComponentUser}

              columnOne={true}
              columnTwo={!isMobileVertical}
              columnThree={false}
              columnFour={false}
              columnFive={true}
              mobileFunction={true}

              titleRow1={t('digital.vault.search.title.1')}
              titleRow2={isIntelligentSearch ? t('digital.vault.search.title.3') : t('digital.vault.search.title.2')}
              titleRow5={t('digital.vault.search.title.4')}

              headerColumnOne={headersColumns}
              headerColumnTwo={headersColumns}
              headerColumnFive={headersColumns}

              cellColumnOne={cellColumnOne}
              cellColumnTwo={cellColumnTwo}

              widthColumnOne={isMobileVertical ? '150px' : (showMobile ? '50px' : '90px')}
              widthColumnTwo={showMobile ? '0px' : '200px'}
              widthColumnFive={widthColumnFive}

              minResizableWidth1={'120px'}
              minResizableWidth2={'200px'}
              minResizableWidth5={minResizableWidth5}


              iconOneActive={!showMobile && !showOnlyIcons}
              labelOneActions={!showMobile && !showOnlyIcons ? t('digital.vault.search.view.details') : ''}
              activeDialog1={true}
              btnOpenDialog1={handleOpenDocument}

              iconFifth={true}
              iconFifthActive={true}
              iconSrc={iconGridInfo}
              onFifthActionClick={handleInfoIconClick}

              onItemChange={handlePageChange}
            />
          </Grid>
        </Grid>
      )}

      {/* Modal de detalles del documento */}
      {showInfoModal && (
        <DialogDetailsPure
          children={childrenDialog}
          dialogTitle={t('fileManager.detailsDialog.labels.detailsTitle')}
          showBtnSecondary={true}
          setCloseDialog={handleCloseInfoModal}
        />
      )}

    </Container>
  )
}

export default DigitalVaultSearchView